// frontend/src/pages/ResetPassword.jsx
import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../config/api";
import Swal from "sweetalert2";
import { Lock, Eye, EyeOff, CheckCircle, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isValidToken, setIsValidToken] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlToken = params.get("token");
    const urlEmail = params.get("email");

    if (!urlToken || !urlEmail) {
      setIsValidToken(false);
      Swal.fire({
        icon: "error",
        title: "Link inválido",
        text: "Este link de redefinição de senha é inválido. Solicite um novo.",
        confirmButtonColor: "#449779",
        customClass: {
          popup: "text-sm rounded-xl",
        },
      }).then(() => navigate("/login"));
      return;
    }

    setToken(urlToken);
    setEmail(decodeURIComponent(urlEmail));
  }, [location, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== passwordConfirmation) {
      Swal.fire({
        icon: "error",
        title: "Senhas não coincidem",
        text: "As senhas digitadas não são iguais.",
        confirmButtonColor: "#449779",
        customClass: {
          popup: "text-sm rounded-xl",
        },
      });
      return;
    }

    if (password.length < 6) {
      Swal.fire({
        icon: "error",
        title: "Senha muito curta",
        text: "A senha deve ter no mínimo 6 caracteres.",
        confirmButtonColor: "#449779",
        customClass: {
          popup: "text-sm rounded-xl",
        },
      });
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_BASE_URL}/password/reset`, {
        email,
        token,
        password,
        password_confirmation: passwordConfirmation,
      });

      Swal.fire({
        icon: "success",
        title: "Senha redefinida!",
        text: "Sua senha foi alterada com sucesso. Faça login com sua nova senha.",
        confirmButtonColor: "#449779",
        customClass: {
          popup: "text-sm rounded-xl",
        },
      }).then(() => navigate("/login"));
    } catch (error) {
      const message = error.response?.data?.message || "Erro ao redefinir senha. Tente novamente.";
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: message,
        confirmButtonColor: "#449779",
        customClass: {
          popup: "text-sm rounded-xl",
        },
      });
      
      if (message.includes("expirado") || message.includes("inválido")) {
        setTimeout(() => navigate("/login"), 3000);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isValidToken) return null;

  return (
    <div className="max-w-md mx-auto px-4 py-8">
      {/* Logo e título */}
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-[#449779] rounded-2xl flex items-center justify-center shadow-lg">
            <span className="text-white text-2xl font-bold">BV</span>
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-800">Redefinir senha</h1>
        <p className="text-sm text-gray-500 mt-1">
          Crie uma nova senha para sua conta
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nova senha
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Mínimo 6 caracteres"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449779] focus:border-transparent"
              required
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5 text-gray-400" />
              ) : (
                <Eye className="w-5 h-5 text-gray-400" />
              )}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            ✓ Mínimo 6 caracteres
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Confirmar nova senha
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Digite a senha novamente"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449779] focus:border-transparent"
              required
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              {showConfirmPassword ? (
                <EyeOff className="w-5 h-5 text-gray-400" />
              ) : (
                <Eye className="w-5 h-5 text-gray-400" />
              )}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-[#449779] text-white py-3 rounded-xl font-semibold hover:bg-[#2d5f4d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Redefinindo...
            </div>
          ) : (
            <>
              <CheckCircle className="w-5 h-5" />
              Redefinir senha
            </>
          )}
        </button>

        <div className="text-center">
          <Link
            to="/login"
            className="text-sm text-gray-500 hover:text-[#449779] transition-colors inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-3 h-3" />
            Voltar para o login
          </Link>
        </div>
      </form>
    </div>
  );
}